import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable, of, throwError } from 'rxjs';
import { catchError, concatMap, filter, map, take, tap } from 'rxjs/operators';

import { ToastService } from '@libs/src/toast/toast.service';

import {
  AddPage,
  GetPage,
  Initialize,
  MEMBERS_DELETE_CATEGORIES_TYPE,
  MEMBERS_GET_INITIAL_PAGE_TYPE,
  MEMBERS_GET_PAGE_TYPE,
  MEMBERS_UPDATE_SEARCH_PARAMS_TYPE,
  MembersAction,
  ResetResults,
} from '@libs/src/accounts/members.actions';
import {
  MembersSearchParams,
  MembersState,
  VIEW_MODES,
} from '@libs/src/accounts/members.interfaces';
import {
  UpdateGroupError,
  UpdateGroupSuccess,
} from '@libs/src/groups/groups-store/groups.actions';
import { Group } from '@libs/src/models/group.model';
import { interests } from '@main-client/src/app/members/members.component.helper';

import { IAppState } from '../app.state';

import get from 'lodash-es/get';
import isEqual from 'lodash-es/isEqual';
import reduce from 'lodash-es/reduce';

const ROOT_MEMBERS_API_URL = '/api/admin/accounts';
const GroupMembersApiv2Url = (group: Group) =>
  `/api/v2/groups/${group._id}/accounts`;
const GroupMembersApiv3Url = (group: Group) =>
  `/api/v3/groups/${group._id}/accounts`;
const DEFAULT_GET_PARAMS: MembersSearchParams = {
  limit: 10,
};
const HTTP_FORBIDDEN_STATUS = 403;

export interface AccountsGetResponse {
  accounts: any[];
  count: number;
  items: any[];
  data: {
    results: any[];
  };
  total_count: number;
}

@Injectable()
export class MembersEffects {
  initialize: Observable<MembersAction> = createEffect(() =>
    this.actions.pipe(
      ofType(MEMBERS_GET_INITIAL_PAGE_TYPE),
      concatMap(() => this.store.select('members').pipe(take(1))),
      concatMap((membersState: MembersState) =>
        this.fetchAccounts(membersState, {
          ...membersState.searchParams,
          skip: 0,
        }),
      ),
      map((response: AccountsGetResponse) => {
        return Initialize(response);
      }),
    ),
  );

  fetchPage: Observable<MembersAction> = createEffect(() =>
    this.actions.pipe(
      ofType(MEMBERS_GET_PAGE_TYPE),
      concatMap((action: MembersAction) =>
        this.store.select('members').pipe(
          take(1),
          map((memberState: MembersState) => ({ action, memberState })),
        ),
      ),
      concatMap(({ action, memberState }: any) => {
        const pageIndex = action.payload.pageIndex;
        const skip = DEFAULT_GET_PARAMS.limit * pageIndex;
        return this.fetchAccounts(memberState, {
          ...memberState.searchParams,
          skip,
        }).pipe(
          map((response: AccountsGetResponse) =>
            AddPage({ ...response, pageIndex }),
          ),
          catchError((error: HttpErrorResponse) => throwError(error)),
        );
      }),
    ),
  );

  updateWithFilter: Observable<MembersAction> = createEffect(() =>
    this.actions.pipe(
      ofType(MEMBERS_UPDATE_SEARCH_PARAMS_TYPE),
      concatMap(() => this.store.select('members').pipe(take(1))),
      filter((memberState: MembersState) => memberState.isInitialized),
      tap(() => this.store.dispatch(ResetResults())),
      map(() => GetPage({ pageIndex: 0 })),
    ),
  );

  removeGroupAccountsCategories: Observable<MembersAction> = createEffect(() =>
    this.actions.pipe(
      ofType(MEMBERS_DELETE_CATEGORIES_TYPE),
      concatMap((action: MembersAction) => {
        const payload = action.payload;
        return (
          interests === payload.type
            ? this.http.patch(
                `${GroupMembersApiv3Url(payload.group)}/remove-interests`,
                payload.categories,
              )
            : this.http.patch(
                `${GroupMembersApiv3Url(payload.group)}/remove-goals`,
                payload.categories,
              )
        ).pipe(
          map(() => {
            this.toastService.showSuccess('Update successful!');
            return UpdateGroupSuccess({ group: payload.group });
          }),
          catchError((errorResponse: HttpErrorResponse) => {
            this.toastService.showError(
              errorResponse.error.message ||
                'Something wrong with updating group accounts.',
            );
            return of(UpdateGroupError({ group: payload.group }));
          }),
        );
      }),
    ),
  );

  constructor(
    private actions: Actions,
    public http: HttpClient,
    public store: Store<IAppState>,
    private toastService: ToastService,
  ) {}

  fetchAccounts(
    membersState: MembersState,
    searchParams: MembersSearchParams,
  ): Observable<any> {
    const params: { [key: string]: string } = reduce(
      { ...DEFAULT_GET_PARAMS, ...searchParams },
      (accumulator, value, key) => {
        return {
          ...accumulator,
          [key]: value,
        };
      },
      {},
    );

    if (isEqual(VIEW_MODES.cards, membersState.viewMode) && params.skip) {
      params.offset = params.skip;
    }

    if (membersState.isAdminMode && params.isAdmin && params.group) {
      params['groups.isAdmin'] = params.isAdmin;
    }

    if (params.group && membersState.isAdminMode) {
      params.order = '-1';
      params.sort = 'groups.join_date';

      if (params.keyword) {
        params.full_name = params.keyword;
        params.email = params.keyword;
        params.slug = params.keyword;
      }
    }
    const fetchUrl = this.getFetchUrl(
      membersState.searchParams,
      membersState.isAdminMode,
    );
    return this.http.get(fetchUrl, { params }).pipe(
      map((response: AccountsGetResponse) => {
        const accounts =
          response.items || response.accounts || get(response, 'data.results');
        const count =
          response.total_count || response.count || get(response, 'data.count');
        return {
          ...response,
          accounts,
          count,
        };
      }),
      catchError((error: any) => this.handleError(error)),
    );
  }

  private getFetchUrl(searchParams: MembersSearchParams, isAdminMode: boolean) {
    const groupSearchParam = searchParams.group;
    if (groupSearchParam) {
      return isAdminMode
        ? GroupMembersApiv2Url({ _id: groupSearchParam })
        : GroupMembersApiv3Url({ _id: groupSearchParam });
    }
    return ROOT_MEMBERS_API_URL;
  }

  private handleError(error: any) {
    if (isEqual(HTTP_FORBIDDEN_STATUS, error.status)) {
      this.toastService.showError('You are restricted to view this page.');
      return of(ResetResults());
    }
    this.toastService.showError('Something went wrong with your search.');
    return of({});
  }
}
